class SubcollectionGrid extends HTMLElement {
  constructor() {
    super()
    this.boundHandleLoadMore = this.handleLoadMore.bind(this)
  }

  connectedCallback() {
    this.button = this.querySelector('[data-subcollection-load-more-button]')
    if (this.button) {
      this.button.addEventListener('click', this.boundHandleLoadMore)
    }
    this.syncSectionIdentity()
  }

  // `sections/subcollection.liquid` can't reliably read this block's resolved
  // `settings.collection` via `section.blocks` (file-based blocks rendered through
  // `content_for 'blocks'` don't expose it to the parent the way inline-schema blocks
  // do), so the section wrapper falls back to a generic id/blank title. This block
  // *can* resolve its own `collection` setting (see `data-collection-handle`/`-title`
  // above), so copy it onto the ancestor `[data-subcollection-section]` here instead -
  // needed for in-page hash links (Anchor Carousel) and the Anchor Targets nav label.
  syncSectionIdentity() {
    const handle = this.dataset.collectionHandle
    const title = this.dataset.collectionTitle
    if (!handle) return

    const section = this.closest('[data-subcollection-section]')
    if (!section) return

    section.id = handle
    section.dataset.subcollectionTitle = title || ''
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.boundHandleLoadMore)
    }
  }

  async handleLoadMore() {
    const url = this.dataset.loadMoreUrl
    const initialCount = parseInt(this.dataset.initialCount, 10) || 0
    const list = this.querySelector('[data-subcollection-grid-list]')
    const firstSuppressedItem = this.getFirstHiddenSuppressedItem()
    const previousItemCount = list ? list.children.length : 0

    this.revealHiddenItems()
    this.setButtonLoading(true)

    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.status}`)
      }

      const html = await response.text()
      const fetchedDoc = new DOMParser().parseFromString(html, 'text/html')
      const fetchedList = fetchedDoc.querySelector('[data-subcollection-grid-list]')

      if (!fetchedList || !list) {
        throw new Error('Could not find the subcollection grid list to append to')
      }

      const remainingItems = Array.from(fetchedList.children).slice(initialCount)
      remainingItems.forEach((item) => list.appendChild(item))

      this.removeLoadMoreControl()
      this.focusAfterLoadMore(firstSuppressedItem || (list && list.children[previousItemCount]))
    } catch (error) {
      console.error(error)
      this.setButtonLoading(false)
    }
  }

  revealHiddenItems() {
    this.querySelectorAll('[data-suppress]').forEach((item) => {
      item.removeAttribute('data-suppress')
    })
  }

  // `[data-suppress]` items marked "mobile" are actually visible on desktop (and vice
  // versa for "desktop") - the attribute alone doesn't say whether an item is hidden at
  // the *current* breakpoint. Check the computed style instead of matching the value.
  getFirstHiddenSuppressedItem() {
    return Array.from(this.querySelectorAll('[data-suppress]')).find(
      (item) => getComputedStyle(item).display === 'none'
    )
  }

  // Removing the load more button drops focus to <body>. Move it to the first newly
  // visible product instead - whichever comes first, a locally revealed suppressed
  // item or a freshly fetched one - so keyboard/screen-reader users can keep going.
  focusAfterLoadMore(target) {
    if (!target) return

    const focusable = target.querySelector('a[href], button, input, select, textarea, [tabindex]')
    if (focusable) {
      focusable.focus()
      return
    }

    target.setAttribute('tabindex', '-1')
    target.focus()
  }

  setButtonLoading(isLoading) {
    if (!this.button) return
    this.button.classList.toggle('is-loading', isLoading)
    this.button.disabled = isLoading
  }

  removeLoadMoreControl() {
    const container = this.querySelector('[data-subcollection-load-more-container]')
    if (container) container.remove()
  }
}

customElements.define('subcollection-grid', SubcollectionGrid)

// Filter/sort toggle: when a filter or non-default sort is applied, hide the
// `Subcollection` sections in favor of `main-collection-product-grid`'s flat results.
// `section-subcollection.css` reacts to the `hidden` attribute directly (via `:has()`),
// so this only needs to keep that attribute in sync - no body class required. Module
// scripts only execute once per URL even though every `subcollection` section instance
// requests this same file, so this runs exactly once per page regardless of how many
// sections are stacked.
function updateGiftGuideFilterState() {
  const sections = document.querySelectorAll('[data-subcollection-section]')
  if (!sections.length) return

  const defaultSortBy = sections[0].dataset.defaultSortBy
  const params = new URLSearchParams(window.location.search)
  let isFiltered = false

  params.forEach((value, key) => {
    if (key.indexOf('filter.') === 0 && key !== 'filter.v.availability' && value !== '') {
      isFiltered = true
    }
  })

  if (!isFiltered) {
    const sortBy = params.get('sort_by')
    if (sortBy && sortBy !== defaultSortBy) {
      isFiltered = true
    }
  }

  sections.forEach((section) => {
    section.hidden = isFiltered
  })
}

updateGiftGuideFilterState()

// `plp:filter-update` is dispatched synchronously from component-filters-wrapper.js's
// cached-render path *before* it calls history.pushState, so window.location.search is
// still the pre-filter URL at the moment listeners run. Deferring to a microtask lets
// that synchronous pushState call (later in the same call stack) finish first.
window.addEventListener('plp:filter-update', () => queueMicrotask(updateGiftGuideFilterState))
window.addEventListener('popstate', updateGiftGuideFilterState)
